import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { PANEL } from "../rendering/designTokens.ts";
import {
  UI_FONT_STACK,
  UI_DURATION,
  UI_EASE,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { makeKeyboardActivatable } from "./a11y.ts";

/**
 * BL-130 — the ONE ⚙ gear dropdown, extracted from the in-game HUD (BL-63) into
 * a reusable component so Boot / PreRun / HUD all carry the SAME top-right gear
 * with the SAME dropdown vocabulary and the SAME canonical row order. Each screen
 * fills it with its own rows (content varies); the order never does.
 *
 * Render-only (Layer 4): the gear shell owns open/close, click-away, Escape, the
 * pop-in (reduced-motion-safe) and the a11y wiring (button `aria-haspopup` /
 * `aria-expanded`; rows are `makeKeyboardActivatable`). It NAMES no game state —
 * callers pass plain `onActivate` handlers.
 */

export interface GearRowOptions {
  /** Row label — a string, or an element the caller keeps a handle on. */
  label: string | HTMLElement;
  /** Optional id (e.g. the HUD mute row's `hud-mute`). */
  id?: string;
  /** Optional keyboard-shortcut hint chip (P · R · Esc). */
  kbd?: string;
  /** Optional trailing element (e.g. a toggle glyph). */
  trailing?: HTMLElement;
  /** Danger styling (the EXIT row). */
  danger?: boolean;
  /** Accessible name override (else derived from a string label). */
  ariaLabel?: string;
  onActivate: () => void;
}

export interface GearMenuOptions {
  /** id/class namespace — `hud` reproduces the HUD gear 1:1; `prerun` / `boot`
   *  get their own self-contained gear. Default `gear`. */
  ns?: string;
  /** Which edge the dropdown aligns to. Default `right` (top-right gear). */
  align?: "left" | "right";
  /** Extra classes on the gear button (the HUD reuses its `.hud-btn` base). */
  buttonClasses?: string[];
  /** Inject the namespaced stylesheet (default true). The HUD passes `false`
   *  and embeds {@link gearMenuStyles} in its own `#hud-styles` sheet so its
   *  existing CSS contract (one stylesheet) is preserved. */
  injectStyles?: boolean;
  /** The gear button carries its own lit-CRT button base (PreRun / Boot have no
   *  shared `.hud-btn`). Default = `injectStyles`. */
  fullButton?: boolean;
  /** Button title / aria-label. Default `t("hud.gear")`. */
  title?: string;
  /** O-11a — called right after the dropdown opens (before focus-first-row).
   *  The HUD uses this to fold pause into the gear itself (combined Pause +
   *  Settings surface); Boot/PreRun simply don't pass it. */
  onOpen?: () => void;
  /** O-11a — called right after the dropdown closes. See {@link onOpen}. */
  onClose?: () => void;
}

export class GearMenu {
  readonly button: HTMLButtonElement;
  readonly menu: HTMLElement;
  private readonly ns: string;
  private open = false;
  private onDocClick: ((e: MouseEvent) => void) | null = null;
  private onKeydown: ((e: KeyboardEvent) => void) | null = null;
  private readonly onOpenCb: (() => void) | null;
  private readonly onCloseCb: (() => void) | null;

  constructor(opts: GearMenuOptions = {}) {
    const ns = opts.ns ?? "gear";
    this.ns = ns;
    this.onOpenCb = opts.onOpen ?? null;
    this.onCloseCb = opts.onClose ?? null;
    const inject = opts.injectStyles ?? true;
    if (inject) ensureGearMenuStyles(ns, opts.fullButton ?? true);

    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.id = `${ns}-gear`;
    this.button.className = [`${ns}-gear-btn`, ...(opts.buttonClasses ?? [])]
      .join(" ");
    this.button.textContent = "⚙";
    const title = opts.title ?? t("hud.gear");
    this.button.title = title;
    this.button.setAttribute("aria-label", title);
    this.button.setAttribute("aria-haspopup", "true");
    this.button.setAttribute("aria-expanded", "false");

    this.menu = document.createElement("div");
    this.menu.id = `${ns}-gear-menu`;
    this.menu.style.display = "none";
    if (opts.align === "left") this.menu.classList.add(`${ns}-gear-menu-left`);

    this.button.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggle();
    });
    // The menu swallows its own clicks so the document click-away (below) only
    // fires for clicks OUTSIDE the gear.
    this.menu.addEventListener("click", (e) => e.stopPropagation());
  }

  /** Append the gear button + dropdown into a POSITIONED container (the menu is
   *  absolutely positioned against it). */
  attach(container: HTMLElement): void {
    container.appendChild(this.button);
    container.appendChild(this.menu);
  }

  /** A `// SECTION` header row. */
  section(title: string): HTMLElement {
    const h = document.createElement("div");
    h.className = `${this.ns}-gear-h`;
    h.textContent = title;
    this.menu.appendChild(h);
    return h;
  }

  /** A thin divider rule. */
  divider(): HTMLElement {
    const d = document.createElement("div");
    d.className = `${this.ns}-gear-div`;
    this.menu.appendChild(d);
    return d;
  }

  /** Append arbitrary content (e.g. the reused audio mixer). */
  appendContent(el: HTMLElement): HTMLElement {
    this.menu.appendChild(el);
    return el;
  }

  /** A clickable, keyboard-operable row. */
  row(opts: GearRowOptions): HTMLElement {
    const row = document.createElement("div");
    row.className = `${this.ns}-gear-row ${this.ns}-gear-act`;
    if (opts.danger) row.classList.add(`${this.ns}-gear-exit`);
    if (opts.id) row.id = opts.id;

    if (typeof opts.label === "string") {
      const span = document.createElement("span");
      span.textContent = opts.label;
      row.appendChild(span);
    } else {
      row.appendChild(opts.label);
    }
    if (opts.kbd) {
      const k = document.createElement("span");
      k.className = `${this.ns}-gear-k`;
      k.textContent = opts.kbd;
      row.appendChild(k);
    }
    if (opts.trailing) row.appendChild(opts.trailing);

    const aria =
      opts.ariaLabel ??
      (typeof opts.label === "string" ? opts.label : undefined);
    if (aria) row.setAttribute("aria-label", aria);
    // S125 a11y — one shared keyboard pattern (role=button + Enter/Space + the
    // focus ring). makeKeyboardActivatable also wires the click handler.
    makeKeyboardActivatable(row, opts.onActivate);
    this.menu.appendChild(row);
    return row;
  }

  isOpen(): boolean {
    return this.open;
  }

  toggle(force?: boolean): void {
    const next = force ?? !this.open;
    if (next === this.open) return;
    this.open = next;
    this.button.setAttribute("aria-expanded", String(next));
    this.button.classList.toggle(`${this.ns}-gear-open`, next);
    this.menu.style.display = next ? "block" : "none";
    if (next) {
      if (!isReducedMotion()) {
        this.menu.classList.remove(`${this.ns}-gear-pop`);
        void this.menu.offsetWidth; // restart the settle animation
        this.menu.classList.add(`${this.ns}-gear-pop`);
      }
      // Close on click-away + Escape (armed only while open).
      this.onDocClick = () => this.close();
      document.addEventListener("click", this.onDocClick);
      this.onKeydown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          this.close();
          this.button.focus();
        }
      };
      document.addEventListener("keydown", this.onKeydown);
      // Focus the first operable row so the dropdown is keyboard-reachable.
      const first = this.menu.querySelector<HTMLElement>(
        'button, [role="button"], input, a[href], [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
      this.onOpenCb?.();
    } else {
      this.detachOpenListeners();
      this.onCloseCb?.();
    }
  }

  close(): void {
    this.toggle(false);
  }

  private detachOpenListeners(): void {
    if (this.onDocClick) document.removeEventListener("click", this.onDocClick);
    if (this.onKeydown) document.removeEventListener("keydown", this.onKeydown);
    this.onDocClick = null;
    this.onKeydown = null;
  }

  destroy(): void {
    this.detachOpenListeners();
    this.button.remove();
    this.menu.remove();
  }
}

const STYLE_IDS = new Set<string>();

/** Inject the namespaced gear stylesheet once. */
export function ensureGearMenuStyles(ns: string, fullButton = true): void {
  if (typeof document === "undefined") return;
  const id = `${ns}-gear-styles`;
  if (STYLE_IDS.has(id) || document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = gearMenuStyles(ns, { fullButton });
  document.head.appendChild(style);
  STYLE_IDS.add(id);
}

/**
 * The gear-dropdown CSS, parameterized by namespace. Ported 1:1 from the BL-63
 * HUD gear so every screen's gear is the same lit-CRT panel. Colours resolve
 * from the central palette (COLORS / PANEL) + `color-mix`; the only literals are
 * the structural `black` / `white` mix anchors (CSS keywords — never inline
 * gameplay hex).
 *
 * `buttonOnly` emits the ⚙ BUTTON rules and nothing else. Spec
 * `2026-pause-settings-overlays` (AC-5) retires the in-game DROPDOWN — the HUD
 * gear now opens the centered settings modal — but the HUD keeps the very same
 * ⚙ button, and it must stay byte-identical to the Boot / Pre-Run gears (that
 * one-gear-everywhere parity is what BL-130 extracted this module for). So the
 * HUD embeds this sheet with `buttonOnly: true`: one source for the button
 * material, and no `#hud-gear-menu { width: 208px; right: 0 }` in the shipped
 * in-game CSS. Boot / Pre-Run pass nothing and get the full dropdown, unchanged.
 */
export function gearMenuStyles(
  ns: string,
  opts: { fullButton?: boolean; buttonOnly?: boolean } = {},
): string {
  const G = COLORS.SENTINEL_GREEN;
  const GD = COLORS.SENTINEL_GREEN_DIM;
  // BL-256 — SENTINEL_GREEN_DIM (~3.1:1) is fine for borders/dividers (WCAG
  // 1.4.11 UI-component 3:1) but sub-AA as TEXT. Section headers, keycap hints
  // and the off-toggle label use the palette's dedicated secondary-text token
  // (TEXT_SECONDARY 4.9:1) instead; GD stays on the non-text chrome.
  const GT = COLORS.TEXT_SECONDARY;
  const greenHi = `color-mix(in srgb, ${G} 48%, white)`;
  const align = `right: 0;`;
  // PreRun / Boot have no shared `.hud-btn` base — give the gear button its own
  // lit-CRT material so it matches the in-game gear exactly.
  const buttonBase = opts.fullButton
    ? `
      color: ${G};
      border: 1px solid color-mix(in srgb, ${G} 45%, transparent);
      border-radius: 6px;
      background: color-mix(in srgb, ${G} 5%, ${PANEL.BG});
      box-shadow: 0 0 9px -4px color-mix(in srgb, ${G} 70%, transparent);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-family: ${UI_FONT_STACK};
      transition: transform ${UI_DURATION.hover}ms ${UI_EASE.snap},
        box-shadow ${UI_DURATION.hover}ms ${UI_EASE.settle},
        border-color ${UI_DURATION.hover}ms ${UI_EASE.settle},
        background ${UI_DURATION.hover}ms ${UI_EASE.settle};`
    : "";
  const buttonHoverBase = opts.fullButton
    ? `
      background: color-mix(in srgb, ${G} 12%, ${PANEL.BG});`
    : "";
  const buttonCss = `
      .${ns}-gear-btn {
        flex-shrink: 0;
        padding: 7px 9px;
        min-width: 36px;
        font-size: ${withUiScale(18)};${buttonBase}
      }
      .${ns}-gear-btn:hover {
        transform: translateY(-1px) rotate(40deg);${buttonHoverBase}
        box-shadow: 0 0 12px -2px color-mix(in srgb, ${G} 55%, transparent);
      }
      .${ns}-gear-btn.${ns}-gear-open {
        border-color: ${G};
        box-shadow: 0 0 12px -2px color-mix(in srgb, ${G} 55%, transparent);
      }
      /* BL-193 — 44px touch floor (was min-width:36px only; cf. TowerSelector.ts:795-798).
         Both axes so the ⚙ tap target is comfortably reachable on touch. */
      @media (pointer:coarse) {
        .${ns}-gear-btn { min-height: 44px; min-width: 44px; }
      }
      @media (prefers-reduced-motion: reduce) {
        .${ns}-gear-btn:hover { transform: none; }
      }`;
  if (opts.buttonOnly) return buttonCss;
  return `${buttonCss}
      /* ── the ⚙ dropdown (mockup '.pop.wide') — a lit-CRT panel over the bar. ── */
      #${ns}-gear-menu {
        position: absolute;
        top: calc(100% + 8px);
        ${align}
        z-index: 30;
        width: 208px;
        padding: 8px;
        background: linear-gradient(180deg, ${PANEL.BG_ELEVATED}, ${PANEL.BG});
        border: 1px solid ${GD};
        border-radius: 8px;
        box-shadow:
          0 0 0 1px black,
          0 14px 40px -16px rgba(0, 0, 0, 0.8),
          0 0 18px color-mix(in srgb, ${G} 12%, transparent);
      }
      #${ns}-gear-menu.${ns}-gear-menu-left { right: auto; left: 0; }
      #${ns}-gear-menu.${ns}-gear-pop {
        animation: ${ns}-gear-pop-settle ${UI_DURATION.settle}ms ${UI_EASE.snap} both;
      }
      @keyframes ${ns}-gear-pop-settle {
        from { opacity: 0; transform: translateY(-8px) scale(0.98); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @media (prefers-reduced-motion: reduce) {
        #${ns}-gear-menu.${ns}-gear-pop { animation: none; }
      }
      .${ns}-gear-h {
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("caption")};
        letter-spacing: 1.5px;
        color: ${GT};
        padding: 1px 4px 4px;
      }
      .${ns}-gear-div {
        height: 1px;
        margin: 3px 2px;
        background: color-mix(in srgb, ${G} 14%, transparent);
      }
      .${ns}-gear-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 6px;
        border-radius: 5px;
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("body")};
        letter-spacing: 0.4px;
        color: ${greenHi};
      }
      .${ns}-gear-act { cursor: pointer; }
      .${ns}-gear-act:hover { background: color-mix(in srgb, ${G} 6%, transparent); }
      .${ns}-gear-row-on { background: color-mix(in srgb, ${G} 6%, transparent); }
      .${ns}-gear-k {
        font-size: ${roleFontSize("micro")};
        color: ${GT};
        border: 1px solid color-mix(in srgb, ${G} 28%, transparent);
        border-radius: 3px;
        padding: 0 5px;
        line-height: 1.5;
      }
      .${ns}-gear-tg { color: ${G}; font-size: ${roleFontSize("body")}; }
      .${ns}-gear-tg.${ns}-gear-tg-off { color: ${GT}; }
      .${ns}-gear-exit { color: ${COLORS.ORACLE_RED}; }
      .${ns}-gear-exit:hover { background: color-mix(in srgb, ${COLORS.ORACLE_RED} 10%, transparent); }
      .${ns}-gear-exit .${ns}-gear-k {
        border-color: color-mix(in srgb, ${COLORS.ORACLE_RED} 35%, transparent);
        color: ${COLORS.ORACLE_RED};
      }
      .${ns}-gear-mixer { padding: 4px 6px 2px; }`;
}
